import React, { useState, useEffect } from 'react';
import { getFirestore, doc, updateDoc, arrayRemove, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import './PropertiesList.css'; // Add a CSS file for styling
import rankingIcon from './imgs/ranking_icon.png'; // Import the image
import binIcon from './imgs/bin.png'; // Import the image
import goldIcon from './imgs/gold.png';
import silverIcon from './imgs/silver.png';

function PropertiesList({ properties, userId, characteristics, onDelete, onUpdate, selectedCharacteristics }) {
  const auth = getAuth();
  const user = auth.currentUser;
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [scores, setScores] = useState({});


  // Function to calculate total score for a property
  const calculateTotalScore = (scores, price) => {
    if (!price || price <= 0) {
      console.warn("Invalid price value, skipping price adjustment.");
      return Object.entries(scores || {}).reduce((total, [characteristic, score]) => {
        const multiplier = selectedCharacteristics[characteristic] || 0; // Default multiplier to 0
        return total + Number(score) * multiplier;
      }, 0);
    }

    // Normalize price (e.g., divide by 1000 for simplicity)
    const normalizedPrice = price / 1000000;

    // Adjust the total score based on the price
    return Object.entries(scores || {}).reduce((total, [characteristic, score]) => {
      const multiplier = selectedCharacteristics[characteristic] || 0; // Default multiplier to 0
      return total + (Number(score) * multiplier) / normalizedPrice;
    }, 0);
  };

  const getCharacteristicName = (id) => {
    const characteristic = characteristics.find((char) => char.id === id);
    return characteristic ? characteristic.name : id; // Fallback to ID if name is not found
  };

  // Sort properties dynamically based on total scores
  const sortedProperties = [...properties].sort((a, b) => {
    return calculateTotalScore(b.scores, b.price) - calculateTotalScore(a.scores, a.price);
  });



  useEffect(() => {
    const fetchCharacteristics = async () => {
      if (!userId) {
        console.error("User ID is missing");
        return;
      }
    };

    if (showScoreModal) {
      fetchCharacteristics();
      if (selectedProperty && selectedProperty.scores) {
        setScores(selectedProperty.scores);
      } else {
        setScores({});
      }
    }

    if (showScoreModal && selectedProperty) {
      setScores(selectedProperty.scores || {}); // Load scores or initialize empty
    }
  }, [userId, showScoreModal, selectedProperty]);

  const handleDelete = async (property) => {
    if (!user) return;

    try {
      const isConfirmed = window.confirm(
        `Are you sure you want to delete "${property.property_name || 'this property'}"?`
      );
      if (isConfirmed) {
        const db = getFirestore();
        const userDocRef = doc(db, "userPreferences", user.uid);
        await updateDoc(userDocRef, {
          properties: arrayRemove(property),
        });
        onDelete && onDelete(property); 
      }
    } catch (error) {
      console.error("Error deleting property:", error);
    }
  };

  const handleScore = (property) => {
    setSelectedProperty({
      ...property,
      scores: property.scores || {}, // Ensure scores exist
    });
    setShowScoreModal(true);
  };

  const handleScoreChange = (characteristic, value) => {
    setScores((prev) => ({
      ...prev,
      [characteristic]: parseInt(value, 10),
    }));
  };

  const handleSubmitScores = async () => {
    if (!selectedProperty || !userId) return;

    try {
      const db = getFirestore();
      const userDocRef = doc(db, "userPreferences", userId);

      // Update the specific property with new scores
      const updatedProperties = properties.map((property) => {
        if (property.url === selectedProperty.url) {
          return { ...property, scores };
        }
        return property;
      });
      onUpdate && onUpdate(updatedProperties); // Update parent state

      // Save the updated properties to Firestore
      await updateDoc(userDocRef, { properties: updatedProperties });
      console.log("Scores saved successfully for", selectedProperty.property_name || "Unnamed Property");

      // Fetch the updated properties from Firestore
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const data = userDocSnap.data();
        onUpdate && onUpdate(data.properties || []); // Notify parent to update the properties
      }
    } catch (error) {
      console.error("Error saving scores:", error);
    }

    setShowScoreModal(false);
  };

  return (
    <div>
      <div className="properties-grid">
        {sortedProperties.map((property, index) => {
          let icon = null;
          if (index === 0) {
            icon = <img src={goldIcon} alt="Top score" className="top-tier-icon" />;
          } else if (index === 1) {
            icon = <img src={silverIcon} alt="Second score" className="top-tier-icon" />;
          }

          return (
            <div
            key={index}
            className="property-card"
            style={{ backgroundImage: `url(${property.property_image})` }}
            onClick={() => handleScore(property)} // Make entire tile clickable
          >
            <div className="overlay">
            {/* Top-left corner for score */}
              <div className="top-left">
                {icon}
                <span className="property-score">
                  {Math.round(calculateTotalScore(property.scores, property.price))}
                </span>
              </div>

              {/* Top-right corner for actions */}
              <div className="top-right">
                <button className="score-button" onClick={(e) => { e.stopPropagation(); handleScore(property); }}>
                  <img src={rankingIcon} alt="Rank this property" className="ranking-icon" />
                </button>
                <button className="delete-button" onClick={(e) => { e.stopPropagation(); handleDelete(property); }}>
                  <img src={binIcon} alt="Delete this property" className="bin-icon" />
                </button>
              </div>

              {/* Bottom-left corner for price and name */}
              <div className="bottom-left">
                <p className="property-price">
                  £{new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(property.price)}
                </p>
                <a
                  href={property.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="property-name-link"
                  onClick={(e) => e.stopPropagation()} // Prevent modal from opening when clicking this link
                >
                  {property.property_name || 'Unnamed Property'}
                </a>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {showScoreModal && selectedProperty && (
        <div className="modal-overlay">
          <div className="score-property">
            <div style={{padding:'20px'}}>
            <h2>{selectedProperty.property_name || "Unnamed Property"}</h2>
            <form>
              <table className="characteristics-table">
                <tbody>
                {Object.entries(selectedCharacteristics)
                  .sort((a, b) => b[1] - a[1]) // Sorting characteristics by score in descending order
                  .map(([char, multiplier]) => (
                    <tr key={char}>
                      <td>{getCharacteristicName(char)}</td>
                      <td>
                        <input
                          type="range"
                          min="1"
                          max="10"
                          style={{
                            background: `linear-gradient(to right, #6867FF 0%, #6867FF ${
                              ((scores[char] || 1) - 1) / 9 * 100
                            }%, #DDDDDD ${((scores[char] || 1) - 1) / 9 * 100}%, #DDDDDD 100%)`,
                          }}
                          value={scores[char] || 1}
                          onChange={(e) => {
                            const newValue = parseInt(e.target.value, 10);
                            handleScoreChange(char, newValue);
                            e.target.style.background = `linear-gradient(to right, #6867FF 0%, #6867FF ${((newValue - 1) / 9) * 100}%, #DDDDDD ${((newValue - 1) / 9) * 100}%, #DDDDDD 100%)`;
                          }}
                        />
                      </td>
                      <td>
                        <span>{scores[char] || 1}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </form>
            </div>
            <div className='scoring-buttons'>
              <button onClick={handleSubmitScores} className="save-button">Save score</button>
              <button onClick={() => setShowScoreModal(false)} className="cancel-score-button" >Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PropertiesList;
